import { useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReportExtractions } from "../../hooks/useReportExtractions";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../components/ui/table";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import {
  formatExtractionStatus,
  ReportExtractionJobResponseType,
  ReportExtractionJobStatusType,
} from "../../models/reportExtraction";
import { formattedDate } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Plus } from "lucide-react";
import { ErrorTooltip } from "../../components/ui/errorTooltip";

export const ReportExtractionsPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // These pages are mounted under both /audit and /dashboard. Audit-disabled
  // workspaces only ever reach the /dashboard copy, and RequireAuthAndAuditEnabled
  // ejects them from /audit, so navigation has to stay in the tree they entered
  // from rather than hardcoding /audit.
  const reportExtractionBase = pathname.startsWith("/audit/")
    ? "/audit/report-extraction"
    : "/dashboard/report-extraction";
  const { jobs, loading, fetchJobs, hasMore, fetchJob } = useReportExtractions({
    fetchOnLoad: true,
  });
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const pendingJobs = jobs.filter(
        (job) => job.status === "pending" || job.status === "running",
      );

      if (pendingJobs.length > 0) {
        for (const job of pendingJobs) {
          fetchJob(job.jobId);
        }
      }
    }, 2500);

    return () => clearInterval(intervalId);
  }, [jobs, fetchJob]);

  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchJobs();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, fetchJobs],
  );

  return (
    <div className="h-screen flex flex-col w-full mx-auto">
      <div className="px-4 mx-auto flex flex-col w-full max-w-4xl mt-24">
        <div className="flex justify-between items-center">
          <div className="text-2xl">Report Extractions</div>
          <Button onClick={() => navigate(`${reportExtractionBase}/new`)}>
            <Plus size={16} className="mr-2" />
            New Extraction
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center items-center mt-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="mt-8 max-h-[75vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Schema</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job, index) => (
                  <TableRow
                    className={`${
                      job.status === "succeeded" ? "cursor-pointer" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (job.status !== "succeeded") {
                        return;
                      }
                      navigate(`${reportExtractionBase}/view/${job.jobId}`);
                    }}
                    key={job.jobId}
                    ref={index === jobs.length - 1 ? lastRowRef : null}
                  >
                    <TableCell
                      className="max-w-xs truncate"
                      title={job.documentName ?? undefined}
                    >
                      {job.documentName ?? "—"}
                    </TableCell>
                    <TableCell>{job.schemaId}</TableCell>
                    <StatusCell job={job} />
                    <TableCell>
                      {job.createdAt ? formattedDate(job.createdAt) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {jobs.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                No report extractions yet. Create your first extraction to get
                started.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Dot colour per status. Keyed by the status type rather than `string` so a new
 * enum member is a compile error here as well as in the label map — otherwise
 * adding one ships a status with a silent grey fallback.
 *
 * `running` is blue to match the workpaper header's pill, which is where the
 * same job is shown next.
 */
const STATUS_DOT_COLORS: Record<ReportExtractionJobStatusType, string> = {
  succeeded: "bg-green-500",
  running: "bg-blue-500",
  // Queued for a worker rather than doing work — muted, and no pulse below.
  pending: "bg-stone-400",
  failed: "bg-red-500",
};

// A status this UI does not map still renders its label; the dot falls back to
// the muted tone rather than claiming a meaning we do not know.
const UNMAPPED_STATUS_DOT = "bg-stone-400";

const StatusCell = ({ job }: { job: ReportExtractionJobResponseType }) => {
  const status = job.status;
  return (
    <TableCell className="flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        {status === "running" && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${STATUS_DOT_COLORS[status as ReportExtractionJobStatusType] ?? UNMAPPED_STATUS_DOT}`}
        ></span>
      </span>
      {formatExtractionStatus(status)}
      {status === "failed" && job.errorMessage && (
        <ErrorTooltip message={job.errorMessage} />
      )}
    </TableCell>
  );
};
